// authorName — a printed author string, parsed at the boundary into a PERSON
// or an ORGANISATION, so a byline shortens only people: "Richard Bird" ->
// "Bird", "Bird, Richard" -> "Bird", "Oege de Moor" -> "de Moor", but
// "Natur & Kultur", "National Academies of Sciences" and "Skolverket,
// enheten för läroplaner" stay whole. Pure.

export type AuthorName =
    | { readonly type: "person"; readonly family: string }
    | { readonly type: "organisation"; readonly name: string };

// Lowercase words that sit inside a family name ("de Moor", "van der Berg").
const PARTICLES = new Set([
    "af",
    "da",
    "de",
    "del",
    "den",
    "der",
    "di",
    "du",
    "la",
    "le",
    "ten",
    "ter",
    "van",
    "von",
]);

// Words and word parts no person's name has.
const JOINERS = /^(&|och|and|of|for|för|für|und|et|i|på|vid)$/u;
const ORG_PARTS =
    /(verket|förlag|institut|universitet|university|akademi|academ|förbund|myndighet|avdelning|kommun|region|department|ministry|council|association|society|foundation|stiftelse|press|^ab$|^inc\.?$|^ltd\.?$)/u;

const isCapitalised = (word: string): boolean =>
    word.charAt(0) !== word.charAt(0).toLowerCase();

// 1–4 words, every one capitalised or a family-name particle, none an
// organisation's.
const looksLikePerson = (words: readonly string[]): boolean =>
    words.length >= 1 &&
    words.length <= 4 &&
    words.every(
        (w) =>
            !JOINERS.test(w.toLowerCase()) &&
            !ORG_PARTS.test(w.toLowerCase()) &&
            (isCapitalised(w) || PARTICLES.has(w)),
    );

const wordsOf = (s: string): readonly string[] =>
    s.split(/\s+/u).filter((w) => w !== "");

export const parseAuthor = (raw: string): AuthorName => {
    const name = raw.trim();
    const organisation: AuthorName = { type: "organisation", name };
    const comma = name.indexOf(",");
    if (comma > 0) {
        // "Family, Given" — both sides shaped like a person's.
        const family = name.slice(0, comma).trim();
        const given = name.slice(comma + 1).trim();
        return looksLikePerson(wordsOf(family)) &&
            looksLikePerson(wordsOf(given))
            ? { type: "person", family }
            : organisation;
    }
    const words = wordsOf(name);
    const last = words.at(-1) ?? "";
    if (words.length < 2 || !looksLikePerson(words) || !isCapitalised(last))
        return organisation;
    // Walk back from the last word over the particles before it.
    const start = words
        .slice(0, -1)
        .reduceRight(
            (at, w, i) => (at === i + 1 && PARTICLES.has(w) ? i : at),
            words.length - 1,
        );
    return { type: "person", family: words.slice(start).join(" ") };
};

// What a byline or the `author:` suggestions cite: a person's family name,
// an organisation's whole name.
export const citedName = (raw: string): string => {
    const a = parseAuthor(raw);
    return a.type === "person" ? a.family : a.name;
};
